import { Backlight, Band, BRAND_RAMP, Eyebrow, Lead, SectionTitle } from '@/components/bedrock';
import { useT } from '@/core/I18nProvider';

import { BRANCHES, STAGE } from './story-data';
import { StoryBranch } from './StoryBranch';
import { StoryWidget } from './StoryWidget';
import { useStoryStage } from './useStoryStage';

import './story.css';

import type { ReactElement } from 'react';

/**
 * The story band — the landing page's flagship demo.
 *
 * A ~21-second one-shot on a black stage: the widget assembles itself from a
 * drawn wireframe, connectors draw out of its edge, and six branches are each
 * born as a bare composer that types a question, sends it, and extrudes a
 * result card upward. Then it settles and lives.
 *
 * ## Accessibility: the stage is a picture, and is treated as one
 *
 * Read literally, the stage is six fabricated chat transcripts and forty
 * fragments of fake product UI. To a screen reader that is not a demo, it is
 * ~400 words of noise with no navigable structure and no way to tell that none
 * of it is real. So the stage is `inert` (removed from the accessibility tree
 * AND from the tab order — the composers look focusable and are not) and the
 * band carries a real prose summary in its place, which says what the picture
 * shows rather than transcribing it.
 *
 * This follows the landing's existing convention for its mock surfaces; note
 * that `pnpm a11y:inert-contrast` exists precisely because axe cannot see
 * inside an `inert` subtree, so the colours in here are checked by that script
 * rather than by the axe pass.
 */
export function Story(): ReactElement {
  const { t } = useT();
  const stage = useStoryStage();

  return (
    <Band
      tone="black"
      id="story"
      space="none"
      outerClassName="overflow-hidden"
      className="!max-w-none !px-0 pb-[70px] pt-[66px]"
      aria-labelledby="story-title"
    >
      {/* Ambient: a brand wash, a masked dot field, and a soft core glow. */}
      <div
        aria-hidden="true"
        data-bd-anim
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(74% 52% at 50% 0%, rgba(43,98,255,.155), transparent 66%),' +
            'radial-gradient(48% 42% at 8% 32%, rgba(22,210,255,.075), transparent 70%),' +
            'radial-gradient(52% 46% at 92% 42%, rgba(181,17,224,.085), transparent 70%)',
          animation: 'bdGlow 12s ease-in-out infinite',
        }}
      />
      <div
        aria-hidden="true"
        className="bd-band-dots pointer-events-none absolute inset-0 opacity-[.42]"
      />

      {/* The band is `!px-0` so the STAGE can bleed, which means the header has
          to rebuild the column itself — and it was rebuilding it on a `px-6`
          that matched neither of the site's two left rules, putting this title
          at 119px against the how band's 151px directly below it. These two
          steps are the card rule spelled out: the page gutter, then the column,
          then the padding a card gives its own content. Structurally the same
          as `<Band align="card">` rather than a number tuned to one viewport —
          the two padding regimes (max-width binding, and not) need different
          numbers, so a single hand-picked padding aligns at 1440px and drifts
          at 800px. */}
      <div className="relative px-5 sm:px-8">
        <div className="mx-auto w-full max-w-[var(--bd-maxw)] px-9 sm:px-14">
          <div className="max-w-[760px]">
            <Eyebrow onDark>{t('home.story.eyebrow')}</Eyebrow>
            <SectionTitle
              id="story-title"
              onDark
              className="mt-3 !text-[clamp(1.55rem,3.2vw,2.5rem)] !leading-[1.06]"
            >
              {t('home.story.title')}
            </SectionTitle>
            <Lead onDark className="mt-3.5 max-w-[62ch]">
              {t('home.story.lead')}
            </Lead>
          </div>
        </div>
      </div>

      {/* The prose alternative to the stage. Visually hidden, but a real
          paragraph — this is what the demo MEANS, for anyone who cannot watch
          it assemble. */}
      <p className="sr-only">{t('home.story.alt')}</p>

      <div
        ref={stage.rootRef}
        data-story-scroller
        className="relative mt-6 overflow-hidden px-5"
        style={{ height: stage.height }}
      >
        {/* `inert` removes the whole mock from the a11y tree AND the tab order.
            React 19 types it as a boolean; on 18 it must be an attribute, so it
            is spread rather than written as a prop. */}
        <div
          data-bd-story
          data-story-stage
          data-armed={stage.armed}
          aria-hidden="true"
          {...{ inert: '' }}
          className="relative origin-top-left rtl:origin-top-right"
          style={{
            width: STAGE.w,
            height: STAGE.h,
            transform: stage.transform,
          }}
        >
          {/* Behind everything in the stage's own coordinate space, so it
              scales with the components rather than drifting off them when a
              narrow viewport shrinks the stage. The pool is centred on the
              widget (STAGE 1618x860, WIDGET centre 809x430 — dead centre), and
              the branches sit inside its falloff. */}
          <Backlight className="-inset-x-24 -inset-y-16" />
          <StoryConnectors />
          <StoryWidget />
          {BRANCHES.map((branch) => (
            <StoryBranch key={branch.id} branch={branch} />
          ))}
        </div>
      </div>
    </Band>
  );
}

/**
 * The connectors: six curved strokes drawn out of the widget's edge, each
 * followed by a travelling spark. Every stroke is painted three times — a wide
 * soft halo, the stroke itself, and a short bright dash that runs its length —
 * which is what makes a line read as CARRYING something rather than as being
 * drawn.
 */
function StoryConnectors(): ReactElement {
  return (
    <svg
      data-story-deco
      width={STAGE.w}
      height={STAGE.h}
      viewBox={`0 0 ${STAGE.w} ${STAGE.h}`}
      fill="none"
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 overflow-visible"
    >
      <defs>
        {(['a', 'b', 'c'] as const).map((id, gi) => (
          <linearGradient key={id} id={`sxG${id}`} x1="0" y1="0" x2="1" y2="1">
            {[0, 0.34, 0.67, 1].map((offset, si) => (
              <stop
                key={offset}
                offset={offset}
                stopColor={BRAND_RAMP[si]}
                style={{
                  animation: `sxFlowHue ${9 + gi * 2}s linear ${-(si * 2.3 + gi * 0.5)}s infinite`,
                }}
              />
            ))}
          </linearGradient>
        ))}
      </defs>

      {BRANCHES.map((branch) => {
        const { path, grad } = branch.placement;
        // Connectors draw just BEFORE their branch's composer appears, so the
        // line arrives first and the composer looks like what it delivered.
        const at = branch.at - 1.0;
        return (
          <g key={branch.id}>
            <path
              data-line
              d={path}
              stroke={`url(#sxG${grad})`}
              strokeWidth="6"
              strokeLinecap="round"
              fill="none"
              opacity=".16"
              style={{
                strokeDasharray: 'var(--len)',
                animation: `sxDraw .85s cubic-bezier(.4,0,.2,1) ${at.toFixed(2)}s backwards`,
              }}
            />
            <path
              data-line
              d={path}
              stroke={`url(#sxG${grad})`}
              strokeWidth="2"
              strokeLinecap="round"
              fill="none"
              style={{
                strokeDasharray: 'var(--len)',
                animation: `sxDraw .85s cubic-bezier(.4,0,.2,1) ${at.toFixed(2)}s backwards`,
              }}
            />
            <path
              data-line
              d={path}
              stroke="#bff0ff"
              strokeWidth="2.6"
              strokeLinecap="round"
              fill="none"
              opacity="0"
              style={{
                strokeDasharray: 'calc(var(--len) * .06) calc(var(--len) * .94)',
                animation:
                  `sxSpark .85s cubic-bezier(.4,0,.2,1) ${at.toFixed(2)}s backwards,` +
                  `sxSparkFlash .85s linear ${at.toFixed(2)}s backwards,` +
                  // After the build, a slow spark keeps travelling each line so
                  // the finished diagram reads as live rather than as a still.
                  `sxSpark 3.2s linear 12.6s infinite, sxFade .6s linear 12.6s forwards`,
              }}
            />
          </g>
        );
      })}
    </svg>
  );
}
